import { useMemo } from "react"
import { useOutletContext } from "react-router-dom"
import Typography from "@mui/material/Typography"
import Grid from "@mui/material/Grid"
import Stack from "@mui/material/Stack"
import { Box, CircularProgress, useMediaQuery, useTheme } from "@mui/material"
import { DateTime } from "luxon"
import { useTranslation } from "react-i18next"

import type { DeviceDashboardOutletContext } from "types/dashboard.types"
import type { PowerlineDevicePositionPlotValues } from "types/devicePosition.types"

import {
  getPowerlinePlotTooltipContent,
  getPowerlinePositionViewVariant,
  getPowerlineViewsSrcs,
} from "helpers/utils/devicePositionPowerline"
import { formatPlotTitleTooltip } from "helpers/utils/plots"
import { degreesToString } from "helpers/utils/common"
import { MD_ROW_HEIGHT, SM_ROW_HEIGHT } from "helpers/utils/constants"
import { useDashboardContext } from "helpers/hooks/useDashboardContext"
import PlotHelpTooltip from "widgets/plots/PlotHelpTooltip"
import { PlotContainer, PositionBox } from "widgets/styled/containers"
import { PowerlineAngleView } from "./PowerlineAngleView"
import PositionGrid from "./PositionGrid"

interface PowerlineDevicePositionPlotProps {
  values: PowerlineDevicePositionPlotValues
  isLoading?: boolean
}

const PowerlineDevicePositionPlot = ({
  values,
  isLoading = false,
}: PowerlineDevicePositionPlotProps) => {
  const theme = useTheme()
  const { device } = useOutletContext<DeviceDashboardOutletContext>()
  const isSmall = useMediaQuery(theme.breakpoints.only("sm"))
  const { rowHeight: contextRowHeight } = useDashboardContext()
  const rowHeight = contextRowHeight ?? (isSmall ? SM_ROW_HEIGHT : MD_ROW_HEIGHT)
  const { alpha, beta, _time } = values
  const { t } = useTranslation()

  const variant = useMemo(() => getPowerlinePositionViewVariant(device), [device])

  const tooltipContent = useMemo(
    () => formatPlotTitleTooltip(getPowerlinePlotTooltipContent(), t),
    [t],
  )

  const { firstViewSrc, secondViewSrc } = useMemo(
    () => getPowerlineViewsSrcs(variant),
    [variant],
  )

  return (
    <PlotContainer
      type=""
      disableGutters
      maxWidth="xl"
      sx={{
        height: { xs: "auto", sm: `${rowHeight * 5}px` },
        marginTop: "1rem",
        marginLeft: 0,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        justifyContent="space-between"
        mb="0.5rem"
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h5">{t("plot_titles.LAST_POSITION") + ":"}</Typography>
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "nowrap", fontWeight: 400 }}
            >
              {DateTime.fromMillis(_time).toFormat("yyyy-MM-dd HH:mm:ss")}
            </Typography>
            {isLoading && <CircularProgress size={20} />}
          </Stack>
        </Box>
        {tooltipContent && <PlotHelpTooltip content={tooltipContent} />}
      </Stack>

      <PositionBox
        sx={{
          overflow: { xs: "visible", sm: "hidden" },
          height: { xs: "auto", sm: "95%" },
          pb: { xs: "2rem", sm: 0 },
        }}
      >
        <PositionGrid />
        <Grid container height={{ xs: "auto", sm: "100%" }} spacing={{ xs: 5, sm: 1 }}>
          <Grid item xs={12} sm={6}>
            <PowerlineAngleView
              src={firstViewSrc}
              rowHeight={rowHeight}
              value={alpha}
              viewKey="first"
              displayName={`α = ${degreesToString(alpha)}`}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <PowerlineAngleView
              src={secondViewSrc}
              rowHeight={rowHeight}
              value={beta}
              viewKey="second"
              displayName={`β = ${degreesToString(beta)}`}
            />
          </Grid>
        </Grid>
      </PositionBox>
    </PlotContainer>
  )
}

export default PowerlineDevicePositionPlot
